import { computed, onMounted, onUnmounted, ref } from 'vue';
import { Device } from '@capacitor/device';

export interface DeviceInfo {
  platform: string;
  model: string;
  operatingSystem: string;
  osVersion: string;
  manufacturer: string;
  isVirtual: boolean;
  webViewVersion: string;
}

export const useMobile = () => {
  const screenWidth = ref(window.innerWidth);
  const deviceInfo = ref<DeviceInfo | null>(null);

  // Check if running on native mobile platform via Capacitor
  const isNativeMobile = computed(() => {
    try {
      // Check if we're in a Capacitor native environment
      return !!(window as any).Capacitor && !!(window as any).Capacitor.isNativePlatform();
    } catch {
      return false;
    }
  });

  // Check if running on mobile-sized screen (responsive breakpoint)
  const isMobileScreen = computed(() => screenWidth.value < 1024); // lg breakpoint

  // Check if mobile interface should be used (either native mobile or small screen)
  const isMobile = computed(() => isNativeMobile.value || isMobileScreen.value);

  // Check if running on iOS native
  const isIOS = computed(() => {
    try {
      return !!(window as any).Capacitor && (window as any).Capacitor.getPlatform() === 'ios';
    } catch {
      return false;
    }
  });

  // Check if running on Android native
  const isAndroid = computed(() => {
    try {
      return !!(window as any).Capacitor && (window as any).Capacitor.getPlatform() === 'android';
    } catch {
      return false;
    }
  });

  // Check if running in web browser
  const isWeb = computed(() => {
    try {
      return !!(window as any).Capacitor && (window as any).Capacitor.getPlatform() === 'web';
    } catch {
      return true; // Default to web if Capacitor is not available
    }
  });

  const updateScreenWidth = () => {
    screenWidth.value = window.innerWidth;
  };

  const getDeviceInfo = async () => {
    try {
      if (isNativeMobile.value) {
        const info = await Device.getInfo();
        deviceInfo.value = {
          platform: info.platform,
          model: info.model,
          operatingSystem: info.operatingSystem,
          osVersion: info.osVersion,
          manufacturer: info.manufacturer,
          isVirtual: info.isVirtual,
          webViewVersion: info.webViewVersion,
        };
      }
    } catch (error) {
      console.warn('Could not get device info:', error);
    }
  };

  onMounted(() => {
    window.addEventListener('resize', updateScreenWidth);
    getDeviceInfo();
  });

  onUnmounted(() => {
    window.removeEventListener('resize', updateScreenWidth);
  });

  return {
    // Mobile detection states
    isMobile,
    isMobileScreen,
    isNativeMobile,
    isIOS,
    isAndroid,
    isWeb,

    // Device information
    deviceInfo,
    screenWidth,

    // Methods
    getDeviceInfo,
  };
};
